import { useState } from "react";
import { useLayoutContext } from "@/components/Layout";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { formatUsd } from "@/lib/format";
import { toUserMessage } from "@/lib/errors";
import {
  useBudget,
  useSetBudget,
  useSetLogging,
} from "@/api/hooks/use-governance";
import { PerUserPolicies } from "@/components/PerUserPolicies";
import { useOnboarding } from "@/api/hooks/use-onboarding";

const inputCls =
  "w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none";
const labelCls =
  "block text-xs font-medium text-fg-muted uppercase tracking-wider mb-1";
const btnPrimary =
  "rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white hover:bg-electric-blue/80 disabled:opacity-50 transition-colors";
const sectionCls =
  "rounded-lg border border-line-subtle bg-surface p-5 space-y-4";
const sectionTitle =
  "text-sm font-medium text-fg-muted uppercase tracking-wider";
const feedbackSuccess =
  "rounded-md border border-safe-green/30 bg-safe-green/10 px-4 py-2 text-sm text-safe-green";
const feedbackError =
  "rounded-md border border-block-red/30 bg-block-red/10 px-4 py-2 text-sm text-block-red";
const noteCls =
  "text-xs text-fg-muted italic";

export function Governance() {
  const { tenantId } = useLayoutContext();
  const budget = useBudget(tenantId);
  const setBudget = useSetBudget();
  const setLogging = useSetLogging();
  // Hidden for a Customer that signed up on its own: a Self-serve Customer is
  // always Collaborative (ADR-0003), and a Priority Customer chose its data
  // terms at signup (ADR-0004); content retention follows them.
  const { data: onboarding } = useOnboarding(true);
  const termsFromSignup = onboarding?.lane === "self_serve" || Boolean(onboarding?.self_signup);

  // Budget form
  const [budgetValue, setBudgetValue] = useState("");
  const [budgetFeedback, setBudgetFeedback] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  // Logging form
  const [loggingEnabled, setLoggingEnabled] = useState(true);
  const [loggingFeedback, setLoggingFeedback] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  if (!tenantId) {
    return <EmptyState message="No tenant associated with your account" />;
  }

  if (budget.isLoading && !budget.data) {
    return <LoadingSkeleton cards={1} rows={3} />;
  }

  if (budget.isError) {
    return (
      <ErrorState
        message="Failed to load governance data"
        onRetry={() => budget.refetch()}
      />
    );
  }

  function clearFeedbackAfter(
    setter: (v: null) => void,
    ms = 5000
  ) {
    setTimeout(() => setter(null), ms);
  }

  // ── Budget handlers ─────────────────────────────────────────────
  function handleSetBudget(e: React.FormEvent) {
    e.preventDefault();
    const num = parseFloat(budgetValue);
    if (isNaN(num) || num < 0) {
      setBudgetFeedback({ type: "error", msg: "Enter a valid budget amount" });
      clearFeedbackAfter(setBudgetFeedback);
      return;
    }
    setBudget.mutate(
      { tenantId, monthly_budget_usd: num },
      {
        onSuccess: () => {
          setBudgetFeedback({ type: "success", msg: "Budget updated" });
          setBudgetValue("");
          clearFeedbackAfter(setBudgetFeedback);
        },
        onError: (err) => {
          setBudgetFeedback({
            type: "error",
            msg: toUserMessage(err),
          });
          clearFeedbackAfter(setBudgetFeedback);
        },
      }
    );
  }

  // ── Logging handler ─────────────────────────────────────────────
  function handleSetLogging(e: React.FormEvent) {
    e.preventDefault();
    setLogging.mutate(
      { tenantId, content_logging_enabled: loggingEnabled },
      {
        onSuccess: () => {
          setLoggingFeedback({ type: "success", msg: "Content logging preference applied" });
          clearFeedbackAfter(setLoggingFeedback);
        },
        onError: (err) => {
          setLoggingFeedback({
            type: "error",
            msg: toUserMessage(err),
          });
          clearFeedbackAfter(setLoggingFeedback);
        },
      }
    );
  }

  const currentBudget = budget.data?.monthly_budget_usd;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-fg">Governance</h1>

      {/* ── Budget ──────────────────────────────────────────────── */}
      <section className={sectionCls}>
        <h2 className={sectionTitle}>Monthly Budget</h2>
        <p className="text-sm text-fg">
          Current budget:{" "}
          <span className="font-mono font-semibold">
            {currentBudget != null ? formatUsd(currentBudget) : "Not set"}
          </span>
        </p>
        <form onSubmit={handleSetBudget} className="flex items-end gap-3">
          <div className="flex-1">
            <label className={labelCls}>New Monthly Budget (USD)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={budgetValue}
              onChange={(e) => setBudgetValue(e.target.value)}
              placeholder="e.g. 500.00"
              className={inputCls}
            />
          </div>
          <button type="submit" disabled={setBudget.isPending} className={btnPrimary}>
            {setBudget.isPending ? "Saving..." : "Update"}
          </button>
        </form>
        {budgetFeedback && (
          <div role={budgetFeedback.type === "success" ? "status" : "alert"} className={budgetFeedback.type === "success" ? feedbackSuccess : feedbackError}>
            {budgetFeedback.msg}
          </div>
        )}
      </section>

      {/* ── Content Logging ─────────────────────────────────────── */}
      {!termsFromSignup && (
        <section className={sectionCls}>
          <h2 className={sectionTitle}>Content Logging</h2>
          <p className={noteCls}>
            Controls whether full prompts and responses are stored in request logs.
          </p>
          <form onSubmit={handleSetLogging} className="flex items-end gap-3">
            <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
              <input
                type="checkbox"
                checked={loggingEnabled}
                onChange={(e) => setLoggingEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-line bg-canvas text-electric-blue focus:ring-electric-blue accent-electric-blue"
              />
              Enable content logging
            </label>
            <button type="submit" disabled={setLogging.isPending} className={btnPrimary}>
              {setLogging.isPending ? "Saving..." : "Apply"}
            </button>
          </form>
          {loggingFeedback && (
            <div role={loggingFeedback.type === "success" ? "status" : "alert"} className={loggingFeedback.type === "success" ? feedbackSuccess : feedbackError}>
              {loggingFeedback.msg}
            </div>
          )}
        </section>
      )}

      {/* ── Per-user policies (only shown for tenants with 2+ users) ── */}
      <PerUserPolicies tenantId={tenantId} />
    </div>
  );
}
